import { Room, RoomEvent, Track } from 'livekit-client';

export class LiveKitService {
  constructor() {
    this.room = null;
    this.isConnected = false;
    this.messageCallbacks = [];
    this.logCallbacks = [];
    this.connectionCallbacks = [];
  }

  async connect(url, token) {
    try {
      if (this.room) {
        await this.disconnect();
      }

      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      // Setup event listeners
      this.setupEventListeners();

      // Connect to the room
      await this.room.connect(url, token);
      this.isConnected = true;
      
      this.notifyConnection(true);
      
      return this.room;
    } catch (error) {
      console.error('Failed to connect to LiveKit:', error);
      this.isConnected = false;
      this.notifyConnection(false, error);
      throw error;
    }
  }

  setupEventListeners() {
    if (!this.room) return;

    // Connection state changes
    this.room.on(RoomEvent.Connected, () => {
      this.isConnected = true;
      this.notifyConnection(true);
    });

    this.room.on(RoomEvent.Disconnected, (reason) => {
      this.isConnected = false;
      this.notifyConnection(false, reason);
    });

    this.room.on(RoomEvent.Reconnecting, () => {
    });

    this.room.on(RoomEvent.Reconnected, () => {
      this.isConnected = true;
      this.notifyConnection(true);
    });

    // Data messages (for text chat and logs)
    this.room.on(RoomEvent.DataReceived, (payload, participant, topic, kind) => {
      try {
        const decoder = new TextDecoder();
        const data = decoder.decode(payload);
        const message = JSON.parse(data);
        message.type = kind;
        
        
        // Route messages based on type
        if (message.type === 'log') {
          this.notifyLog(message);
        } else {
          this.notifyMessage(message);
        }
      } catch (error) {
        console.error('Error processing LiveKit data:', error);
      }
    });

    // Track subscriptions (for audio)
    this.room.on(RoomEvent.TrackSubscribed, (track) => {
      
      // Disable audio track playback - only receive text responses
      if (track.kind === Track.Kind.Audio) {
        // Don't attach or play audio tracks
        return;
      }
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach().forEach(element => element.remove());
    });
  }

  // Send text message
  async sendMessage(text) {
    if (!this.room || !this.isConnected) {
      throw new Error('Not connected to LiveKit room');
    }
    const data = JSON.stringify({type: "user_text_input", text: text});
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    await this.room.localParticipant.publishData(encodedData, {
      reliable: true,
    }).then(() => {
    });
  }

  // Send audio data
  async sendAudioData(audioBlob) {
    if (!this.room || !this.isConnected) {
      throw new Error('Not connected to LiveKit room');
    }

    // Convert blob to array buffer
    const arrayBuffer = await audioBlob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    await this.room.localParticipant.publishData(uint8Array, {
      reliable: false, // Audio can tolerate some packet loss
    });
    
  }

  // Callback management
  onMessage(callback) {
    this.messageCallbacks.push(callback);
  }

  onLog(callback) {
    this.logCallbacks.push(callback);
  }

  onConnection(callback) {
    this.connectionCallbacks.push(callback);
  }

  notifyMessage(message) {
    this.messageCallbacks.forEach(cb => cb(message));
  }

  notifyLog(log) {
    this.logCallbacks.forEach(cb => cb(log));
  }

  notifyConnection(isConnected, error = null) {
    this.connectionCallbacks.forEach(cb => cb(isConnected, error));
  }

  // Disconnect
  async disconnect() {
    if (this.room) {
      await this.room.disconnect();
      this.room = null;
      this.isConnected = false;
    }
  }

  // Cleanup
  cleanup() {
    this.messageCallbacks = [];
    this.logCallbacks = [];
    this.connectionCallbacks = [];
  }
}

export default LiveKitService;
